import { Box, Button } from "@mui/material"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/system"
import { SessionState, VideoPlatform } from "app-constants/constants"
import AspectRatio from "components/common/AspectRatio"
import BlurredBackgroundImage from "components/common/BlurredBackgroundImage"
import DetailsRowText from "components/common/DetailsRowText"
import Whereby from "components/sessions/Whereby"
import useConventionSessionRealTimeCapacity from "hooks/sessions/useConventionSessionRealTimeCapacity"
import useJoinSession from "hooks/sessions/useJoinSession"
import useSessionCopyText from "hooks/sessions/useSessionCopyText"
import useSessionState from "hooks/sessions/useSessionState"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import React from "react"
import { pxToRem } from "utils/helpers"

interface SessionStageProps {
  session: ConventionSession
  className?: string
}

const SessionStage = (props: SessionStageProps) => {
  const { session, className } = props
  const imageSrc = session.promotional_image_url

  const sessionCopyText = useSessionCopyText()
  const joinSession = useJoinSession(session)
  const realTimeCapacity = useConventionSessionRealTimeCapacity(session)
  const sessionState = useSessionState({ session, joinSession, realTimeCapacity })

  if (joinSession.isJoined && session.video_platform === VideoPlatform.WHEREBY) {
    return (
      <_Container ratio={69 / 41} className={className}>
        <Whereby meetingUrl={session.integrated_meeting_url} />
      </_Container>
    )
  }
  return (
    <_Container ratio={69 / 41} className={className}>
      <BlurredBackgroundImage imageSrc={imageSrc} className={className}>
        <_Content>
          <_MainContent>
            <ConventionExhibitorNameText>
              {session.convention_exhibitor?.exhibitor_name}
            </ConventionExhibitorNameText>
            <_SessionNameText>{session.name}</_SessionNameText>
            <_DetailsRowText
              startAt={session.start_at}
              endAt={session.end_at}
              platform={session.video_platform}
            />
          </_MainContent>
          {sessionState === SessionState.AT_CAPACITY && (
            <_SecondaryContent>
              <_HelperText>
                This {sessionCopyText?.singular} is currently at capacity. You can check back in
                later as seats may become available.
              </_HelperText>
            </_SecondaryContent>
          )}
          {sessionState === SessionState.ENDED && (
            <_SecondaryContent>
              <_HelperText>This {sessionCopyText?.singular} has ended.</_HelperText>
            </_SecondaryContent>
          )}
          {sessionState === SessionState.ZOOM && (
            <_SecondaryContent>
              <_HelperText>
                This {sessionCopyText.singular} will be held on Zoom. At session start time, click
                on the Join Session button and follow the prompts.
              </_HelperText>
            </_SecondaryContent>
          )}
          {sessionState === SessionState.READY_TO_JOIN && (
            <_SecondaryContent>
              <_JoinLiveSessionButton variant="contained" onClick={joinSession.join}>
                JOIN {sessionCopyText?.singular}
              </_JoinLiveSessionButton>
            </_SecondaryContent>
          )}
        </_Content>
      </BlurredBackgroundImage>
    </_Container>
  )
}

export default SessionStage

export const _Container = styled(AspectRatio)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  borderRadius: pxToRem(10),
}) as typeof AspectRatio

export const _Content = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexFlow: "column nowrap",
  width: "100%",
  height: "100%",
  textAlign: "center",
  padding: "20px",
  [theme.breakpoints.up("sm")]: {
    gap: "15px",
    padding: "75px 0",
  },
  [theme.breakpoints.up("md")]: {
    padding: "110px 0",
  },
})) as typeof Box

const _MainContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "flex-start",
  alignItems: "center",
  maxWidth: "600px",
  gap: "10px",
  [theme.breakpoints.up("sm")]: {
    gap: "15px",
  },
  [theme.breakpoints.up("md")]: {
    gap: "30px",
  },
})) as typeof Box

const _SecondaryContent = styled(Box)(({}) => ({
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "flex-end",
  alignItems: "center",
  maxWidth: "500px",
})) as typeof Box

export const _BaseText = styled(Typography)(({ theme }) => ({
  color: theme.palette.gray.lightest,
  textAlign: "center",
  margin: 0,
  padding: 0,
})) as typeof Typography

const ConventionExhibitorNameText = styled(_BaseText)(({ theme }) => ({
  ...theme.fonts.lato400,
  fontSize: "14px",
  lineHeight: "17px",
  textTransform: "uppercase",
})) as typeof _BaseText

const _SessionNameText = styled(_BaseText)(({ theme }) => ({
  ...theme.fonts.raleway500,
  lineHeight: "1.16",
  fontSize: "20px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "25px",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "36px",
  },
})) as typeof _BaseText

const _DetailsRowText = styled(DetailsRowText)(({ theme }) => ({
  display: "none",
  justifyContent: "center",
  textAlign: "center",
  color: theme.palette.gray.lightest,
  [theme.breakpoints.up("sm")]: {
    display: "flex",
  },
})) as typeof DetailsRowText

const _HelperText = styled(_BaseText)(({ theme }) => ({
  ...theme.fonts.lato400,
  fontSize: "16px",
  lineHeight: "22px",
})) as typeof _BaseText

const _JoinLiveSessionButton = styled(Button)(({ theme }) => ({
  padding: "9px 87.5px",
  borderRadius: "10px",
  backgroundColor: theme.palette.gray.lightest,
  color: theme.palette.gray.darkest,
  ...theme.fonts.lato700,
  fontSize: "14px",
  lineHeight: "17px",
  boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25), 0px 4px 4px rgba(0, 0, 0, 0.25)",
  "&:hover": {
    backgroundColor: theme.palette.gray.light,
  },
}))
